import React, { useState, useCallback } from "react";
import {
  Button,
  Box,
  Modal,
  Typography,
  LinearProgress,
  IconButton,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import * as XLSX from "xlsx";
import axios from "../../api";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import { CloudUpload, Download, Description, Close } from "@mui/icons-material";

interface BulkUploadAssetProps {
  isOpen: boolean;
  onClose: () => void;
  fetchData: () => Promise<void>;
}

interface ParsedAsset {
  customer_id: number;
  asset_type_id: number;
  brand_name: string;
  address_id: number;
  asset_purchased_date: string | number | Date;
}

const BulkUploadAssets = ({
  isOpen,
  onClose,
  fetchData,
}: BulkUploadAssetProps) => {
  const { fetchError, fetchSuccess } = useSnackbar();

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ParsedAsset[]>([]);

  // 📥 Download Template
  const downloadTemplate = () => {
    const template = [
      {
        "Customer Id": "1",
        "Asset Type Id": "1",
        "Brand Name": "Brand Name Example",
        "Asset Purchased Date": "2024-01-15",
        "Address Id": "1",
      },
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Assets Template");

    ws["!cols"] = [
      { wch: 15 },
      { wch: 15 },
      { wch: 25 },
      { wch: 22 },
      { wch: 15 },
    ];

    XLSX.writeFile(wb, "assets_bulk_upload_template.xlsx");
  };

  // ✅ Validate & Normalize
  const validateAndNormalize = (row: any, index: number): ParsedAsset => {
    if (
      !row.customer_id ||
      !row.asset_type_id ||
      !row.brand_name ||
      !row.asset_purchased_date ||
      !row.address_id
    ) {
      throw new Error(`Row ${index + 1}: Missing required fields`);
    }

    let dateValue = row.asset_purchased_date;

    // Excel Serial Number Date → String Conversion
    if (typeof dateValue === "number") {
      const jsDate = new Date((dateValue - 25569) * 86400 * 1000);
      dateValue = jsDate.toISOString().split("T")[0];
    }

    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (typeof dateValue !== "string" || !dateRegex.test(dateValue)) {
      throw new Error(
        `Row ${index + 1}: Invalid date format (must be YYYY-MM-DD)`
      );
    }

    return {
      ...row,
      asset_purchased_date: dateValue,
    };
  };

  // 📤 Handle File Drop - Fixed to avoid returning Promise
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      setSelectedFile(file);

      // Wrap async logic in IIFE to avoid returning Promise
      void (async () => {
        try {
          // Use arrayBuffer() instead of FileReader
          const buffer = await file.arrayBuffer();
          const data = new Uint8Array(buffer);

          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet);

          const normalizedData = jsonData.map((row) => ({
            customer_id: Number(row["Customer Id"]) || "",
            asset_type_id: Number(row["Asset Type Id"]) || "",
            brand_name: row["Brand Name"] || "",
            asset_purchased_date: row["Asset Purchased Date"] || "",
            address_id: Number(row["Address Id"]) || "",
          }));

          const validatedData = normalizedData.map(validateAndNormalize);
          setParsedData(validatedData);
        } catch (error: any) {
          console.error(error);
          fetchError(
            error.message || "Error parsing file. Check format and fields."
          );
          setSelectedFile(null);
        }
      })();
    },
    [fetchError]
  );

  // 🚀 Upload final data
  const handleUpload = async () => {
    if (!parsedData.length) {
      fetchError("No file data found. Please upload a valid Excel/CSV file.");
      return;
    }

    setUploading(true);
    setProgress(20);

    try {
      const response = await axios.post("/assets/bulk-upload", {
        assets: parsedData,
      });

      setProgress(100);
      fetchSuccess(response.data.message || "Assets uploaded successfully!");
      fetchData?.();

      setTimeout(() => handleClose(), 1500);
    } catch (error: any) {
      console.error(error);
      fetchError(
        error.message || "Upload failed. Please check the file and try again."
      );
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
      "text/csv": [".csv"],
    },
    multiple: false,
  });

  const handleClose = () => {
    setProgress(0);
    setUploading(false);
    setParsedData([]);
    setSelectedFile(null);
    onClose();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setParsedData([]);
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          borderRadius: "8px",
          boxShadow: 24,
          p: 4,
          minWidth: 600,
          maxWidth: "90vw",
          textAlign: "center",
        }}
      >
        <Typography variant="h5" sx={{ mb: 2 }}>
          Bulk Upload Assets
        </Typography>

        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={downloadTemplate}
          sx={{ mb: 3 }}
        >
          Download Template
        </Button>

        <Box
          {...getRootProps()}
          sx={{
            border: "2px dashed #ccc",
            borderRadius: "8px",
            p: 4,
            cursor: "pointer",
            backgroundColor: isDragActive ? "#f0f0f0" : "transparent",
            "&:hover": { backgroundColor: "#f9f9f9" },
          }}
        >
          <input {...getInputProps()} />
          <CloudUpload sx={{ fontSize: 48, color: "#666", mb: 2 }} />
          <Typography>
            {isDragActive
              ? "Drop the file here"
              : "Drag and drop an Excel/CSV file here, or click to select"}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Supported formats: .xlsx, .xls, .csv
          </Typography>
        </Box>

        {/* 📄 File Preview */}
        {selectedFile && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: "8px",
              bgcolor: "#f5f5f5",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              border: "1px solid #ddd",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Description color="primary" />
              <Typography variant="body2">{selectedFile.name}</Typography>
            </Box>
            <IconButton size="small" onClick={handleRemoveFile}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        )}

        {uploading && (
          <Box sx={{ mt: 3 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Uploading... {progress}%
            </Typography>
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "center", mt: 4, gap: 2 }}>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
          >
            Save & Upload
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default BulkUploadAssets;
